import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Edit, Trash2, X, Save, Plus, Upload, Loader, Star, StarOff,
  Image as ImageIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'tiger', label: '🐯 Tiger' },
  { value: 'leopard', label: '🐆 Leopard' },
  { value: 'birds', label: '🦅 Birds' },
  { value: 'landscape', label: '🌳 Landscape' },
  { value: 'group', label: '👥 Group Photo' },
  { value: 'wildlife', label: '🦌 Other Wildlife' },
];

const emptyForm = {
  image_url: '',
  caption: '',
  customer_name: '',
  location: '',
  safari_date: '',
  category: 'tiger',
  featured: false,
  is_active: true,
  order: 0,
};

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const multiUploadRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API}/admin/gallery`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file) => {
    const token = localStorage.getItem('adminToken');
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API}/upload`, fd, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data.url;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((prev) => ({ ...prev, image_url: url }));
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Bulk upload — quick-add multiple photos at once (no captions, can edit later)
  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBulkUploading(true);
    const token = localStorage.getItem('adminToken');
    let success = 0;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      try {
        const url = await uploadFile(f);
        await axios.post(
          `${API}/admin/gallery`,
          {
            image_url: url,
            caption: '',
            customer_name: '',
            location: '',
            safari_date: '',
            category: 'tiger',
            featured: false,
            is_active: true,
            order: 0,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        success++;
      } catch (err) {
        console.error('Bulk upload error:', err);
      }
    }
    setBulkUploading(false);
    if (multiUploadRef.current) multiUploadRef.current.value = '';
    toast.success(`${success} photo${success !== 1 ? 's' : ''} added to gallery!`);
    fetchItems();
  };

  const openNew = () => {
    setEditing('new');
    setForm(emptyForm);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      image_url: item.image_url || '',
      caption: item.caption || '',
      customer_name: item.customer_name || '',
      location: item.location || '',
      safari_date: item.safari_date || '',
      category: item.category || 'tiger',
      featured: !!item.featured,
      is_active: item.is_active !== false,
      order: item.order || 0,
    });
  };

  const handleSave = async () => {
    if (!form.image_url) {
      toast.error('Please upload or provide an image');
      return;
    }
    const token = localStorage.getItem('adminToken');
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/gallery`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Gallery item added!');
      } else {
        await axios.put(`${API}/admin/gallery/${editing}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Gallery item updated!');
      }
      setEditing(null);
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this photo from gallery?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const toggleFeatured = async (item) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(
        `${API}/admin/gallery/${item.id}`,
        {
          image_url: item.image_url,
          caption: item.caption || '',
          customer_name: item.customer_name || '',
          location: item.location || '',
          safari_date: item.safari_date || '',
          category: item.category || 'tiger',
          featured: !item.featured,
          is_active: item.is_active !== false,
          order: item.order || 0,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchItems();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-body text-muted-foreground">Loading gallery...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-gallery">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl text-navy">Safari Library</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">
            Customer & in-house safari photos to build trust with visitors.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => multiUploadRef.current?.click()}
            disabled={bulkUploading}
            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white px-5 py-3 rounded-lg font-body font-semibold flex items-center space-x-2 shadow-md"
            data-testid="bulk-upload-btn"
          >
            {bulkUploading ? <Loader size={18} className="animate-spin" /> : <Upload size={18} />}
            <span>{bulkUploading ? 'Uploading…' : 'Quick Upload (multiple)'}</span>
          </button>
          <input
            ref={multiUploadRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleBulkUpload}
            className="hidden"
          />
          <button
            onClick={openNew}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3 rounded-lg font-body font-semibold flex items-center space-x-2 shadow-md"
            data-testid="new-gallery-btn"
          >
            <Plus size={18} />
            <span>Add Photo with Details</span>
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <ImageIcon size={48} className="mx-auto text-emerald-300 mb-4" />
          <p className="font-body text-navy text-lg mb-1">No photos yet</p>
          <p className="font-body text-sm text-muted-foreground">
            Use "Quick Upload" to add multiple photos at once, or "Add Photo with Details" for full info.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-md overflow-hidden group"
              data-testid={`gallery-item-${item.id}`}
            >
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={resolveImageUrl(item.image_url)}
                  alt={item.caption || 'Safari'}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400?text=Invalid';
                  }}
                />
                {item.featured && (
                  <div className="absolute top-2 left-2 bg-amber-400 text-emerald-900 text-xs font-bold px-2 py-1 rounded-full flex items-center space-x-1">
                    <Star size={12} fill="currentColor" />
                    <span>FEATURED</span>
                  </div>
                )}
                <div className="absolute top-2 right-2 bg-emerald-700/90 text-white text-xs px-2 py-1 rounded-full">
                  {CATEGORIES.find((c) => c.value === item.category)?.label || item.category}
                </div>
                {!item.is_active && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-white font-semibold uppercase text-xs tracking-wider">Hidden</span>
                  </div>
                )}
              </div>
              <div className="p-3 space-y-1">
                {item.caption && (
                  <p className="font-body text-sm text-navy line-clamp-2 font-medium">
                    {item.caption}
                  </p>
                )}
                {item.customer_name && (
                  <p className="font-body text-xs text-emerald-700">— {item.customer_name}</p>
                )}
                {item.location && (
                  <p className="font-body text-xs text-muted-foreground">📍 {item.location}</p>
                )}
              </div>
              <div className="flex items-center justify-between border-t border-border px-3 py-2 bg-sand/30">
                <button
                  onClick={() => toggleFeatured(item)}
                  className="p-1.5 hover:bg-amber-100 rounded transition"
                  title={item.featured ? 'Unfeature' : 'Mark as featured'}
                >
                  {item.featured ? (
                    <Star size={16} className="text-amber-500" fill="currentColor" />
                  ) : (
                    <StarOff size={16} className="text-gray-400" />
                  )}
                </button>
                <div className="flex space-x-1">
                  <button
                    onClick={() => openEdit(item)}
                    className="p-1.5 hover:bg-emerald-100 rounded transition"
                    title="Edit"
                    data-testid={`edit-gallery-${item.id}`}
                  >
                    <Edit size={16} className="text-navy" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 hover:bg-red-100 rounded transition"
                    title="Delete"
                  >
                    <Trash2 size={16} className="text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-emerald-700 p-6 flex items-center justify-between z-10">
              <h2 className="font-heading text-2xl text-amber-300">
                {editing === 'new' ? 'Add Safari Photo' : 'Edit Photo'}
              </h2>
              <button
                onClick={() => setEditing(null)}
                className="text-white hover:text-amber-300"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Image upload */}
              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  Photo *
                </label>
                <div className="flex items-stretch space-x-2 mb-2">
                  <input
                    type="url"
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    className="flex-1 border border-border rounded-lg px-4 py-2 font-body"
                    placeholder="Image URL or upload →"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white px-4 rounded-lg font-body font-semibold flex items-center space-x-1"
                    data-testid="single-upload-btn"
                  >
                    {uploading ? <Loader size={18} className="animate-spin" /> : <Upload size={18} />}
                    <span>{uploading ? 'Uploading…' : 'Upload'}</span>
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
                {form.image_url && (
                  <img
                    src={resolveImageUrl(form.image_url)}
                    alt="preview"
                    className="w-full h-56 object-cover rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x600?text=Invalid';
                    }}
                  />
                )}
              </div>

              {/* Caption */}
              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  Caption
                </label>
                <input
                  type="text"
                  value={form.caption}
                  onChange={(e) => setForm({ ...form, caption: e.target.value })}
                  className="w-full border border-border rounded-lg px-4 py-2 font-body"
                  placeholder="e.g. Majestic tiger sighting at Moharli Gate"
                />
              </div>

              {/* Customer name + location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-body text-sm font-medium text-navy mb-2">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    className="w-full border border-border rounded-lg px-4 py-2 font-body"
                    placeholder="e.g. Rahul S., Mumbai"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm font-medium text-navy mb-2">
                    Location / Zone
                  </label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full border border-border rounded-lg px-4 py-2 font-body"
                    placeholder="e.g. Moharli Gate"
                  />
                </div>
              </div>

              {/* Category + Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-body text-sm font-medium text-navy mb-2">
                    Category
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-border rounded-lg px-4 py-2 font-body"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-body text-sm font-medium text-navy mb-2">
                    Safari Date
                  </label>
                  <input
                    type="date"
                    value={form.safari_date}
                    onChange={(e) => setForm({ ...form, safari_date: e.target.value })}
                    className="w-full border border-border rounded-lg px-4 py-2 font-body"
                  />
                </div>
              </div>

              {/* Flags */}
              <div className="flex flex-wrap gap-6 bg-sand/40 p-4 rounded-lg">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    className="w-4 h-4 accent-amber-500"
                  />
                  <span className="font-body text-sm text-navy">⭐ Featured (pin to top)</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="w-4 h-4 accent-emerald-600"
                  />
                  <span className="font-body text-sm text-navy">👁 Visible on public site</span>
                </label>
                <label className="flex items-center space-x-2">
                  <span className="font-body text-sm text-navy">Order:</span>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                    className="w-20 border border-border rounded px-2 py-1 font-body"
                  />
                </label>
              </div>

              <div className="flex space-x-4 pt-2">
                <button
                  onClick={() => setEditing(null)}
                  className="flex-1 bg-muted text-navy px-6 py-3 rounded-lg font-body font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  data-testid="save-gallery-btn"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-body font-semibold flex items-center justify-center space-x-2 shadow-md"
                >
                  <Save size={20} />
                  <span>{editing === 'new' ? 'Add to Gallery' : 'Save Changes'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
